import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Returns the unified credit balance — sum of `profiles.credits` across the
 * authed user and any linked partner account. The number is identical on
 * every linked device so users never see "different credits" between web and
 * iOS after linking.
 *
 * Falls back to `profile.credits` (self-only) if the RPC hasn't loaded yet.
 */
export function useEffectiveCredits(): {
  credits: number;
  isLoading: boolean;
} {
  const { user, profile } = useAuth();
  const fallback = profile?.credits ?? 0;

  const { data, isLoading } = useQuery({
    queryKey: ["effective-credits", user?.id],
    enabled: !!user?.id,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("get_effective_credits", {
        _user_id: user!.id,
      });
      if (error) {
        console.error("[useEffectiveCredits] rpc error", error);
        return fallback;
      }
      return (data as number) ?? fallback;
    },
  });

  return {
    credits: data ?? fallback,
    isLoading: !!user && isLoading,
  };
}
